import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Anchor, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useSignUp } from '@clerk/clerk-react';

const SignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    assetAccountName: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    acceptedTerms: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      acceptedTerms: checked
    }));
  };

  const validateStep1 = () => {
    if (!formData.assetAccountName.trim()) {
      toast.error('Asset account name is required');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.adminName.trim()) {
      toast.error('Your name is required');
      return false;
    }
    if (!formData.adminEmail.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.adminPassword) {
      toast.error('Password is required');
      return false;
    }
    if (formData.adminPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (formData.adminPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!formData.acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep3()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organizationName: formData.assetAccountName,
          adminName: formData.adminName,
          adminEmail: formData.adminEmail,
          adminPassword: formData.adminPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Account created! Pending approval from our team.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error(data.detail || 'Failed to create account');
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const progressPercentage = (step / 3) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border border-gray-200">
        <CardHeader className="space-y-3 text-center pb-6">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-3 rounded-lg shadow-md">
              <Anchor className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl sm:text-3xl font-bold text-gray-900">
              Create Your Account
            </CardTitle>
            <CardDescription className="text-sm sm:text-base mt-2 text-gray-600">
              Step {step} of 3
            </CardDescription>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div 
              className="bg-gradient-to-r from-blue-600 to-cyan-600 h-2 rounded-full transition-all duration-300 shadow-sm"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Google OAuth Button */}
          <div className="space-y-4 mb-6">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border-2 border-gray-200 hover:bg-gray-50"
              onClick={() => {
                toast.info('Social sign-in will be enabled after your account is approved');
              }}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or continue with email</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Step 1: Asset Account Name */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="assetAccountName" className="text-sm font-medium text-gray-700">
                    Asset Account Name
                  </Label>
                  <Input
                    id="assetAccountName"
                    name="assetAccountName"
                    type="text"
                    placeholder="e.g., Lake Austin Resources"
                    value={formData.assetAccountName}
                    onChange={handleInputChange}
                    className="h-11"
                    required
                    autoFocus
                  />
                  <p className="text-xs text-gray-500">
                    This will be your organization's display name
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={handleNext}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-11"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Admin Details */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="adminName" className="text-sm font-medium text-gray-700">
                    Your Name
                  </Label>
                  <Input
                    id="adminName"
                    name="adminName"
                    type="text"
                    placeholder="John Doe"
                    value={formData.adminName}
                    onChange={handleInputChange}
                    className="h-11"
                    required
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminEmail" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="adminEmail"
                    name="adminEmail"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.adminEmail}
                    onChange={handleInputChange}
                    className="h-11"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 h-11"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white h-11 shadow-md"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Password & Terms */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="adminPassword" className="text-sm font-medium text-gray-700">
                    Password
                  </Label>
                  <Input
                    id="adminPassword"
                    name="adminPassword"
                    type="password"
                    placeholder="At least 8 characters"
                    value={formData.adminPassword}
                    onChange={handleInputChange}
                    className="h-11"
                    required
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Re-enter your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="h-11"
                    required
                  />
                </div>

                {/* Terms Checkbox */}
                <div className="flex items-start space-x-2 py-2">
                  <Checkbox
                    id="acceptedTerms"
                    checked={formData.acceptedTerms}
                    onCheckedChange={handleCheckboxChange}
                    className="mt-1"
                  />
                  <label
                    htmlFor="acceptedTerms"
                    className="text-sm text-gray-600 leading-relaxed cursor-pointer"
                  >
                    I agree to the{' '}
                    <a href="#" className="text-blue-600 hover:underline" onClick={(e) => e.preventDefault()}>
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-blue-600 hover:underline" onClick={(e) => e.preventDefault()}>
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 h-11"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium h-11 shadow-md"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating...</span>
                      </div>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" /> Create Account
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Login Link */}
            <div className="text-center text-sm text-gray-600 pt-2 border-t">
              Already have an account?{' '}
              <Button
                type="button"
                variant="link"
                className="text-blue-600 hover:underline p-0 h-auto font-medium"
                onClick={() => navigate('/login')}
              >
                Log in
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupPage;
